import { GET_COMPANY } from "@apiUtils";
import { mutationFetch } from "@atlas/sdk";
import type { ICompany } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const COMPANY_KEY = "companyKey";

export const getCompanyKey = (companyUuid: string, basePath?: string) =>
  [COMPANY_KEY, companyUuid, basePath] as const;

export const updateCompany = async (
  companyUuid: string,
  request: Partial<ICompany>,
  basePath?: string,
): Promise<ICompany> => {
  const path = `${GET_COMPANY}/${companyUuid}`;
  const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
  return mutationFetch<ICompany>({
    url,
    method: "PATCH",
    body: request,
  });
};

export const useUpdateCompany = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: Partial<
    UseMutationOptions<ICompany, AxiosError, Partial<ICompany>, unknown>
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: Partial<ICompany>) =>
      updateCompany(companyUuid ?? "", request, basePath),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
};

export const useCompany = (
  companyUuid: string | undefined,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      ICompany,
      AxiosError,
      ICompany,
      ReturnType<typeof getCompanyKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getCompanyKey(companyUuid ?? "", basePath),
    queryFn: async () => {
      const path = `${GET_COMPANY}/${companyUuid}`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
      const data = await mutationFetch<ICompany>({
        url,
        method: "GET",
      });
      return data;
    },
    enabled: !!companyUuid,
    ...options,
  });
};
